import React, { ReactNode } from "react";
import styled from "@emotion/styled";
import { useTheme } from "@emotion/react";
import { Box, useMediaQuery } from "@mui/material";
import { AccountTypeEnum } from "@rentablo/api/apiv1";
import { useTranslation } from "react-i18next";
import { MandatorTypography } from "@rentablo/components";
import { MandatorType, useMandatorContext } from "@rentablo/theming";
import AccountPreviewIcon from "./AccountPreviewIcon";

const AccountMetaContainer = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  flex-grow: 1;
`;

const BankAvatarWrapper = styled.div`
  display: flex;
  margin-right: ${({ theme }) => theme.spacing(2)};
  ${({ theme }) => theme.breakpoints.up("sm")} {
    margin-right: ${({ theme }) => theme.spacing(4)};
  }
`;

const AccountNameContainer = styled.div`
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AccountName = styled(MandatorTypography)`
  width: 100%;
`;

const AccountSubtitle = styled(MandatorTypography)`
  width: 100%;
`;

export interface AccountIconAndNameProps {
  isReference?: boolean;
  bankLogoURL: string;
  accountType: AccountTypeEnum;
  accountName: string;
  bankName?: string;
  bankCode?: string;
  nativeCurrencyAnnotation?: string;
  badge?: ReactNode;
  content?: ReactNode;
}

const iconSizeMap = {
  kpmg: 30,
  default: 40,
};

const getIconSize = (mandatorSlug: MandatorType): number => {
  return (
    iconSizeMap[mandatorSlug as keyof typeof iconSizeMap] || iconSizeMap.default
  );
};

export const AccountIconAndName = ({
  isReference,
  bankLogoURL,
  accountType,
  accountName,
  bankName,
  bankCode,
  nativeCurrencyAnnotation,
  badge,
  content,
}: AccountIconAndNameProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { mandatorSlug } = useMandatorContext();
  const iconSize = getIconSize(mandatorSlug);

  const accountNameNode = (
    <AccountName
      color={theme.palette.text.primary}
      variant="subtitle2"
      kpmg-germany={{ variant: "body2" }}
      noWrap
      data-testid="account-name"
    >
      {accountName}
      {nativeCurrencyAnnotation && (
        <MandatorTypography
          component="span"
          variant="subtitle2"
          kpmg-germany={{ variant: "body2" }}
          color="textSecondary"
          ml={1}
        >
          ({nativeCurrencyAnnotation})
        </MandatorTypography>
      )}
    </AccountName>
  );

  return (
    <AccountMetaContainer>
      <BankAvatarWrapper>
        <AccountPreviewIcon
          isReference={isReference ?? false}
          iconUrl={bankLogoURL}
          type={accountType}
          size={iconSize}
        />
      </BankAvatarWrapper>
      <AccountNameContainer>
        {badge ? (
          <Box display="flex" alignItems="center" gap={2} sx={{ minWidth: 0 }}>
            <Box sx={{ minWidth: 0 }}>{accountNameNode}</Box>
            {badge}
          </Box>
        ) : (
          accountNameNode
        )}
        <AccountSubtitle
          variant={isMobile ? "body4" : "body2"}
          lineHeight={isMobile ? "20px" : "24px"}
          color="textSecondary"
          noWrap
        >
          {bankName ?? t("accountView.noBank")}
          {bankCode && ` • ${bankCode}`}
        </AccountSubtitle>
        {content}
      </AccountNameContainer>
    </AccountMetaContainer>
  );
};
